/** The API returns NAIVE UTC strings: "2026-08-18 04:12:09" — no zone marker. */

const IST = 'Asia/Kolkata'
const LOCALE = 'en-IN'

/** Naive-UTC string -> Date. The appended 'Z' is the whole point of this file. */
export function parseApiDate(value: string): Date {
  // Accept both "YYYY-MM-DD HH:MM:SS" and an already-ISO value, so a future
  const iso = value.includes('T') ? value : value.replace(' ', 'T')
  const withZone = /[Zz]|[+-]\d{2}:?\d{2}$/.test(iso) ? iso : `${iso}Z`
  return new Date(withZone)
}

const FORMATS = {
  date:     { day: '2-digit', month: 'short', year: 'numeric' },
  time:     { hour: '2-digit', minute: '2-digit', hour12: true },
  datetime: { day: '2-digit', month: 'short', year: 'numeric',
              hour: '2-digit', minute: '2-digit', hour12: true },
  full:     { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
              hour: '2-digit', minute: '2-digit', hour12: true },
} satisfies Record<string, Intl.DateTimeFormatOptions>

export type DateStyle = keyof typeof FORMATS

/** Render a naive-UTC API value in IST. Every visible timestamp uses this. */
export function formatIST(value: string, style: DateStyle = 'datetime'): string {
  return new Intl.DateTimeFormat(LOCALE, { timeZone: IST, ...FORMATS[style] })
    .format(parseApiDate(value))
}

/** "12m", "3h", "5 Aug" — what actually tells someone a lead is fresh. */
export function relativeTime(value: string, now: Date = new Date()): string {
  const then = parseApiDate(value)
  const seconds = Math.floor((now.getTime() - then.getTime()) / 1000)

  if (seconds < 45) return 'just now'   // negative too: clock skew, not the future
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`
  if (seconds < 86_400) return `${Math.floor(seconds / 3600)}h`
  if (seconds < 7 * 86_400) return `${Math.floor(seconds / 86_400)}d`
  return formatIST(value, 'date')
}

/** Date -> "YYYY-MM-DD HH:MM:SS" in UTC, the format the API expects back. */
export function toNaiveUtc(date: Date): string {
  return date.toISOString().slice(0, 19).replace('T', ' ')
}

/** An IST calendar range -> the naive-UTC bounds the API filters on. */
export function istRangeToUtcParams(from: Date, to: Date): { from: string; to: string } {
  return { from: toNaiveUtc(from), to: toNaiveUtc(to) }
}

/** Start of an IST day N days back, through now, as UTC bounds. */
export function istDaysAgoRange(days: number, now: Date = new Date()): { from: string; to: string } {
  // Shift into IST, truncate to the day, then shift back. Using getHours()
  const istNow = new Date(now.toLocaleString('en-US', { timeZone: IST }))
  const offsetMs = istNow.getTime() - now.getTime()

  const startIst = new Date(istNow)
  startIst.setHours(0, 0, 0, 0)
  startIst.setDate(startIst.getDate() - days)

  return {
    from: toNaiveUtc(new Date(startIst.getTime() - offsetMs)),
    to: toNaiveUtc(now),
  }
}

/** True when a naive-UTC value falls on today's IST calendar date. */
export function isTodayIST(value: string, now: Date = new Date()): boolean {
  const fmt = new Intl.DateTimeFormat('en-CA', { timeZone: IST })
  return fmt.format(parseApiDate(value)) === fmt.format(now)
}

/** Today's IST calendar date as YYYY-MM-DD. */
export function istToday(now: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: IST }).format(now)
}

/**
 * Represents an IST date range filter (YYYY-MM-DD).
 */
export function istDayRange(days: number, now: Date = new Date()): { from: string; to: string } {
  const fmt = new Intl.DateTimeFormat('en-CA', { timeZone: IST })
  const to = fmt.format(now)
  const start = new Date(now.getTime() - (days - 1) * 86_400_000)
  return { from: fmt.format(start), to }
}
